import useSWR from 'swr';
import { UserAccount } from '@webapp/bff/src/types/user';
import { trpc } from '../config/trpc';
import { useAnalytics } from '../hooks/analytics/useAnalytics';

// Separate SWR cache keys for active vs. onboarding users as we don't want
// regular 401s to show up as errors during onboarding
export const USER_SWR_CACHE_KEY = 'loggedin-user-details';
export const ONBOARD_USER_SWR_CACHE_KEY = 'onboard-user';

export default function useCurrentUserData(
  sessionKey: string | null = USER_SWR_CACHE_KEY,
  authToken?: string
) {
  const { track } = useAnalytics();

  const { data, error, isValidating, mutate } = useSWR<UserAccount | undefined>(
    sessionKey,
    () =>
      trpc.user.getUserDetails.query(
        undefined,
        authToken
          ? {
              context: {
                token: authToken,
              },
            }
          : undefined
      ),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
    }
  );
  return {
    data,
    error,
    isValidating,
    updateUserProfile: async (
      firstName: string,
      lastName: string,
      isChangePassword: boolean,
      oldPassword?: string,
      newPassword?: string
    ) => {
      if (firstName !== data?.firstName || lastName !== data?.lastName) {
        await trpc.user.updateUserProfile.mutate({
          firstName,
          lastName,
        });
        track('User Name Updated', {
          firstName,
          lastName,
          referrer: 'User Profile',
        });
      }
      if (isChangePassword) {
        await trpc.user.changePassword.mutate({
          oldPassword: oldPassword ?? '',
          newPassword: newPassword ?? '',
        });
        await track('User Password Changed', {
          firstName: data?.firstName,
          lastName: data?.lastName,
          referrer: 'User Profile',
        });
      }
      await mutate();
    },
    requestPhoneUpdateCode: async (args: { phoneNumber: string }) => {
      await trpc.user.requestMfaCodeForPhoneNumberChange.mutate(args);
      await track('Phone Number Update Requested', {
        phoneNumber: args.phoneNumber,
        referrer: 'User Profile',
      });
    },
    updatePhoneNumber: async (args: { phoneNumber: string; code: string }) => {
      await trpc.user.confirmMfaCodeForPhoneNumberChange.mutate(args);
      await track('Phone Number Updated', {
        phoneNumber: args.phoneNumber,
        referrer: 'User Profile',
      });
    },
    updateProfileImage: async ({
      data64thumb,
      imageFile,
    }: {
      data64thumb: string;
      imageFile: File;
    }) => {
      if (!data?.externalId) {
        throw new Error('User ID not found');
      }
      const { url: uploadUrl, fileId } =
        await trpc.profile.generatePresignedUrl.query({
          userId: data.externalId,
        });
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: imageFile,
        headers: {
          'Content-Type': imageFile.type,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to upload image');
      }
      await trpc.profile.updateProfileMetadata.mutate({
        fileId,
        fileName: imageFile.name,
        fileSize: imageFile.size,
        thumbnailUrl: data64thumb,
      });
      await track('Profile Image Updated', {
        fileName: imageFile.name,
        referrer: 'User Profile',
      });
      await mutate();
    },
    updateNotificationPreference: async (
      channels: ('sms' | 'push' | 'email')[]
    ) => {
      await trpc.user.updateAlertNotifications.mutate({
        notificationsChannels: channels,
      });
      await track('Notification Preference Updated', {
        channels,
        referrer: 'User Notifications Settings',
      });
      await mutate();
    },
  };
}

interface OnboardParams {
  shouldFetch: boolean;
  token?: string;
}

export function useOnboardUser({ shouldFetch, token }: OnboardParams) {
  const fetcher = async () => {
    const response = await trpc.onboard.getUserDetails.query(undefined, {
      context: {
        token,
      },
    });
    return response;
  };

  const { data, error } = useSWR(
    shouldFetch ? ONBOARD_USER_SWR_CACHE_KEY : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  return { data, error, isLoading: !data && !error };
}
